import { useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { countHivMenuClick, hivPagePath } from "@/lib/hivPage";

interface HivNavButtonProps {
  className?: string;
}

/**
 * Menu entry for the sensitive HIV+ page.
 * - Rendered as a <button> (no href in the DOM) so link-click / outbound
 *   tracking has no URL to read.
 * - The visible label comes from a CSS pseudo-element, so "Click Text"
 *   style auto-capture reads no text from the element.
 * - A native click listener stops bubbling before document-level listeners
 *   (GTM, pixels) and before React, then does a full page navigation to the
 *   tracker-free static page (no SPA history push).
 */
export const HivNavButton = ({ className = "" }: HivNavButtonProps) => {
  const { language } = useLanguage();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      countHivMenuClick();
      window.location.assign(hivPagePath(language));
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [language]);

  return (
    <button
      ref={ref}
      type="button"
      aria-label="HIV+"
      className={`font-semibold text-primary transition-colors hover:text-primary/80 animate-soft-glow motion-reduce:animate-none after:content-['HIV+'] ${className}`}
    />
  );
};
